const BASE = import.meta.env.VITE_API_URL ?? "http://127.0.0.1:8000";

// ─── Send a message to the correct agent ─────────────────────────────────────
export async function sendMessageToAgent(
  prompt: string,
  opts?: { sessionId?: string }
) {
  const res = await fetch(`${BASE}/api/chat/agent`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(opts?.sessionId ? { "X-Session-Id": opts.sessionId } : {}),
    },
    body: JSON.stringify({ prompt }),
  });

  const text = await res.text();
  if (!res.ok) throw new Error(text || `HTTP ${res.status}`);

  const json = JSON.parse(text);
  return json.reply as string;
}

// ─── Upload a report ──────────────────────────────────────────────────────────
// Does NOT reset the session — uploading a PGx report keeps the Germline
// report intact, and vice versa. Each report type is stored independently.
export async function uploadReport(file: File, sessionId: string) {
  const fd = new FormData();
  fd.append("file", file);

  const res = await fetch(`${BASE}/api/chat/upload-report`, {
    method: "POST",
    headers: { "X-Session-Id": sessionId },
    body: fd,
  });

  const text = await res.text();
  if (!res.ok) throw new Error(text || `HTTP ${res.status}`);

  return JSON.parse(text) as {
    vector_store_id: string;
    file_id: string;
    report_type: "pgx" | "germline";
    reports_loaded: Array<"pgx" | "germline">;
  };
}

// ─── Get which reports are loaded for this session ────────────────────────────
// Use this to show upload status badges in the UI.
export async function getSessionStatus(sessionId: string) {
  const res = await fetch(`${BASE}/api/chat/session-status`, {
    method: "GET",
    headers: { "X-Session-Id": sessionId },
  });

  if (!res.ok) return { reports_loaded: [] as string[] };

  const json = await res.json();
  return json as { reports_loaded: Array<"pgx" | "germline"> };
}

// ─── Full session reset ───────────────────────────────────────────────────────
// Only call this when the user explicitly wants to clear EVERYTHING and start over.
// Do NOT call this on file upload — each report type is stored independently.
export async function resetSession(sessionId: string) {
  try {
    await fetch(`${BASE}/api/chat/reset-session`, {
      method: "POST",
      headers: { "X-Session-Id": sessionId },
    });
  } catch {
    console.warn("Session reset failed, continuing anyway.");
  }
}
